/**
 * Stage keeps a local stack of polaroids. The current photo is also fetched
 * via `visibleSubmission`. After a reject of that current photo, the stack
 * drops it while `visibleSubmission` can still point at it (or Apollo can
 * still hold the query result). Ignore removed ids and `approved === false`
 * so a stale current-photo value cannot put the image back on Stage —
 * including after a Stage refresh, when the in-memory removed set is empty.
 *
 * When the current photo is already in the stack, Stage still drops: it
 * moves that polaroid to the top and remounts it. Skipping those ticks
 * froze the Stage once N <= stack size, and skipped new arrivals that
 * came back around while still on screen.
 */

import { DEFAULT_MAX_VISIBLE_PHOTOS } from '../consts.ts';

export { DEFAULT_MAX_VISIBLE_PHOTOS };

export function shouldFetchVisibleSubmission({
  visibleSubmissionId,
  stackedSubmissionIds,
  suppressedSubmissionIds,
}: {
  visibleSubmissionId: string | null | undefined;
  stackedSubmissionIds: Array<string | undefined>;
  suppressedSubmissionIds: Iterable<string>;
}): boolean {
  if (!visibleSubmissionId) {
    return false;
  }
  if (new Set(suppressedSubmissionIds).has(visibleSubmissionId)) {
    return false;
  }
  return !stackedSubmissionIds.includes(visibleSubmissionId);
}

export function shouldPromoteStackedSubmission({
  visibleSubmissionId,
  stackedSubmissionIds,
  suppressedSubmissionIds,
}: {
  visibleSubmissionId: string | null | undefined;
  stackedSubmissionIds: Array<string | undefined>;
  suppressedSubmissionIds: Iterable<string>;
}): boolean {
  if (!visibleSubmissionId) {
    return false;
  }
  if (new Set(suppressedSubmissionIds).has(visibleSubmissionId)) {
    return false;
  }
  return stackedSubmissionIds.includes(visibleSubmissionId);
}

export function shouldAppendFetchedSubmission({
  fetchedSubmissionId,
  visibleSubmissionId,
  suppressedSubmissionIds,
  stackedSubmissionIds,
  approved,
}: {
  fetchedSubmissionId: string | null | undefined;
  visibleSubmissionId: string | null | undefined;
  suppressedSubmissionIds: Iterable<string>;
  stackedSubmissionIds?: Array<string | undefined>;
  approved?: boolean | null;
}): boolean {
  if (!fetchedSubmissionId || !visibleSubmissionId) {
    return false;
  }
  if (fetchedSubmissionId !== visibleSubmissionId) {
    return false;
  }
  if (approved === false) {
    return false;
  }
  if (new Set(suppressedSubmissionIds).has(fetchedSubmissionId)) {
    return false;
  }
  if (stackedSubmissionIds?.includes(fetchedSubmissionId)) {
    return false;
  }
  return true;
}

export function removeSubmissionFromStack<T extends { submissionId?: string }>(
  photos: T[],
  submissionId: string,
): T[] {
  return photos.filter((photo) => photo.submissionId !== submissionId);
}

/**
 * Move an already-stacked photo to the top. `remountPhoto` must assign a new
 * instance identity so the polaroid re-animates as a fresh drop.
 */
export function moveStackedSubmissionToTop<T extends { submissionId?: string }>(
  photos: T[],
  submissionId: string,
  remountPhoto: (photo: T) => T,
): T[] {
  const index = photos.findIndex(
    (photo) => photo.submissionId === submissionId,
  );
  if (index === -1) {
    return photos;
  }
  const remounted = remountPhoto(photos[index]);
  return [...photos.slice(0, index), ...photos.slice(index + 1), remounted];
}

export function appendPhotoToStack<T>(
  photos: T[],
  photo: T,
  maxPhotos: number = DEFAULT_MAX_VISIBLE_PHOTOS,
): T[] {
  const next = [...photos, photo];
  if (next.length > maxPhotos) {
    next.shift();
  }
  return next;
}
